import React, { useEffect, useState } from "react";
import { Alert, Badge, Col, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch } from "react-redux";
import { Input } from "../../components";
import { daftarIkon, ikonIndikator } from "../../utils/indicatorIcon";
import {
  bacaMasalah,
  simpanIndikator,
  ubahIndikator,
} from "../../config/redux/action/sustainabilityAction";

const KATEGORI = [
  { id: "environment", name: "Lingkungan" },
  { id: "social", name: "Sosial" },
  { id: "efficiency", name: "Efisiensi" },
];

const CALC_TYPE = [
  { id: "measured", name: "Measured (terukur langsung)" },
  { id: "factor_based", name: "Factor Based (dikali faktor)" },
  { id: "derived", name: "Derived (dari formula)" },
  { id: "manual", name: "Manual (input manual, hanya kategori sosial)" },
];

const AGGREGATION = [
  { id: "sum", name: "Sum" },
  { id: "avg", name: "Average" },
  { id: "weighted_avg", name: "Weighted Average" },
  { id: "ratio", name: "Ratio" },
  { id: "latest", name: "Latest" },
];

const ALLOCATION = [
  { id: "weight", name: "Berat (kg)" },
  { id: "item", name: "Per item" },
  { id: "bag", name: "Per kantong" },
  { id: "order", name: "Per order" },
];

const TIER = [
  { id: "T1", name: "T1 (terukur, terverifikasi)" },
  { id: "T2", name: "T2" },
  { id: "T3", name: "T3" },
  { id: "T4", name: "T4 (estimasi kasar)" },
];

const DISPLAY_SCOPE = [
  { id: "order", name: "Order" },
  { id: "period", name: "Periode" },
  { id: "both", name: "Keduanya" },
];

const SAVING_DIRECTION = [
  { id: "lower_is_better", name: "Lebih rendah lebih baik" },
  { id: "higher_is_better", name: "Lebih tinggi lebih baik" },
];

const kosong = {
  code: "",
  name_id: "",
  name_en: "",
  kategori: "environment",
  unit: "",
  decimal_places: 2,
  icon: "",
  calculation_type: "measured",
  source_fields: "",
  formula_expression: "",
  allocation_basis: "weight",
  aggregation_method: "sum",
  has_saving: false,
  baseline_id: "",
  saving_direction: "lower_is_better",
  display_scope: "both",
  visible_customer: false,
  visible_admin: true,
  min_quality_tier_to_publish: "T3",
  sort_order: 100,
  catatan_perubahan: "",
};

function Seksi({ judul, children }) {
  return (
    <div className="mb-3">
      <div
        className="fw-bold border-bottom pb-1 mb-2"
        style={{ fontSize: "12px" }}
      >
        {judul}
      </div>
      {children}
    </div>
  );
}

function ModalIndikator({ show, handleClose, data, baselineList, formulaVariables, update }) {
  const [Form, setForm] = useState(kosong);
  const [Error, setError] = useState([]);
  const [VersiBaru, setVersiBaru] = useState(false);
  const dispatch = useDispatch();

  const draft = data === null || data.status === "draft";

  useEffect(() => {
    setError([]);
    setVersiBaru(false);
    if (data !== null) {
      setForm({
        ...kosong,
        ...data,
        name_en: data.name_en || "",
        icon: data.icon || "",
        formula_expression: data.formula_expression || "",
        baseline_id: data.baseline_id || "",
        catatan_perubahan: data.catatan_perubahan || "",
        // source_fields disimpan sebagai JSONB array; di form diketik sebagai
        // daftar dipisah koma supaya tidak perlu editor JSON.
        source_fields: Array.isArray(data.source_fields)
          ? data.source_fields.join(", ")
          : "",
      });
    } else {
      setForm(kosong);
    }
  }, [data, show]);

  const ubah = (key, value) => setForm({ ...Form, [key]: value });

  const susunPayload = () => ({
    code: Form.code,
    name_id: Form.name_id,
    name_en: Form.name_en || null,
    kategori: Form.kategori,
    unit: Form.unit,
    decimal_places: Number(Form.decimal_places) || 0,
    icon: Form.icon || null,
    calculation_type: Form.calculation_type,
    source_fields: Form.source_fields
      .split(",")
      .map((val) => val.trim())
      .filter((val) => val !== ""),
    formula_expression: Form.formula_expression || null,
    allocation_basis: Form.allocation_basis,
    aggregation_method: Form.aggregation_method,
    has_saving: !!Form.has_saving,
    baseline_id: Form.baseline_id === "" ? null : Number(Form.baseline_id),
    saving_direction: Form.saving_direction,
    display_scope: Form.display_scope,
    visible_customer: !!Form.visible_customer,
    visible_admin: !!Form.visible_admin,
    min_quality_tier_to_publish: Form.min_quality_tier_to_publish,
    sort_order: Number(Form.sort_order) || 100,
    catatan_perubahan: Form.catatan_perubahan || null,
  });

  const simpan = () => {
    setError([]);
    const payload = susunPayload();
    const aksi =
      data !== null
        ? ubahIndikator(data.id, payload)
        : simpanIndikator({ ...payload, izinkan_versi_baru: VersiBaru });

    dispatch(aksi)
      .then(() => {
        update();
        handleClose();
      })
      .catch((err) => setError(bacaMasalah(err)));
  };

  const dataBaseline = [
    { id: "", name: "— tanpa baseline —" },
    ...(baselineList || [])
      .filter((val) => val.status === "active")
      .map((val) => ({
        id: val.id,
        name: `${val.indicator_code} · ${val.baseline_type} · ${val.nilai} ${val.unit} (${val.quality_tier})`,
      })),
  ];

  return (
    <Modal show={show} onHide={handleClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "16px" }}>
          {data !== null ? `Indikator ${data.code}` : "Tambah Indikator"}
          {data !== null && (
            <>
              <Badge bg="secondary" className="ms-2">
                v{data.version}
              </Badge>
              <Badge
                bg={
                  data.status === "active"
                    ? "success"
                    : data.status === "draft"
                    ? "warning"
                    : "dark"
                }
                className="ms-1"
              >
                {data.status}
              </Badge>
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!draft && (
          <Alert variant="secondary" className="py-2" style={{ fontSize: "12px" }}>
            Indikator berstatus <b>{data.status}</b> tidak dapat diubah. Buat versi
            baru dari tab Registry bila definisinya perlu direvisi.
          </Alert>
        )}
        {Error.length > 0 && (
          <Alert variant="danger" style={{ fontSize: "12px" }}>
            <div className="fw-bold mb-1">Konfigurasi belum valid</div>
            <ul className="mb-0 ps-3">
              {Error.map((val, i) => (
                <li key={i}>{val}</li>
              ))}
            </ul>
          </Alert>
        )}

        <fieldset disabled={!draft}>
          <Row>
            <Col xs={12} lg={6}>
              <Seksi judul="Identitas">
                <Input
                  judul="Code *"
                  type="text"
                  placeholder="ENV_WATER_SAVED"
                  value={Form.code}
                  onChange={(e) => ubah("code", e.target.value.toUpperCase())}
                />
                <Input
                  judul="Nama (ID) *"
                  type="text"
                  value={Form.name_id}
                  onChange={(e) => ubah("name_id", e.target.value)}
                />
                <Input
                  judul="Nama (EN)"
                  type="text"
                  value={Form.name_en}
                  onChange={(e) => ubah("name_en", e.target.value)}
                />
                <Input
                  judul="Kategori"
                  type="select"
                  data={KATEGORI}
                  value={Form.kategori}
                  onChange={(e) => ubah("kategori", e.target.value)}
                />
                <Input
                  judul="Unit *"
                  type="text"
                  placeholder="liter, kWh, ml, g"
                  value={Form.unit}
                  onChange={(e) => ubah("unit", e.target.value)}
                />
                <Input
                  judul="Desimal"
                  type="number"
                  value={Form.decimal_places}
                  onChange={(e) => ubah("decimal_places", e.target.value)}
                />
                {/* Dropdown, bukan isian bebas: nama ikon harus salah satu
                    yang punya padanan aset di aplikasi mobile. Diketik bebas,
                    indikatornya akan tampil tanpa lambang di receipt customer
                    dan tidak ada yang tahu sampai ada yang membuka layarnya. */}
                <div className="mb-2">
                  <div style={{ fontSize: "12px" }} className="fw-bold mb-1">
                    Ikon
                  </div>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={ikonIndikator(Form.icon)}
                      className="me-2 text-secondary"
                    />
                    <select
                      className="form-select form-select-sm"
                      value={Form.icon || ""}
                      onChange={(e) => ubah("icon", e.target.value)}
                    >
                      <option value="">(tanpa ikon)</option>
                      {daftarIkon.map((nama) => (
                        <option key={nama} value={nama}>
                          {nama}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Seksi>

              <Seksi judul="Penghematan">
                <Input
                  judul="Punya Penghematan (has_saving)"
                  type="aktif"
                  checked={!!Form.has_saving}
                  onChange={(e) => ubah("has_saving", e.target.checked)}
                />
                <Input
                  judul="Baseline"
                  type="select"
                  data={dataBaseline}
                  value={Form.baseline_id}
                  onChange={(e) => ubah("baseline_id", e.target.value)}
                />
                <Input
                  judul="Arah Penghematan"
                  type="select"
                  data={SAVING_DIRECTION}
                  value={Form.saving_direction}
                  onChange={(e) => ubah("saving_direction", e.target.value)}
                />
                {Form.has_saving && Form.calculation_type !== "derived" && (
                  <div className="text-muted mb-2" style={{ fontSize: "10px" }}>
                    Indikator dengan penghematan wajib punya baseline aktif untuk
                    kodenya, dan satuan baseline harus sepadan dengan unit di atas.
                  </div>
                )}
              </Seksi>
            </Col>

            <Col xs={12} lg={6}>
              <Seksi judul="Perhitungan">
                <Input
                  judul="Tipe Perhitungan *"
                  type="select"
                  data={CALC_TYPE}
                  value={Form.calculation_type}
                  onChange={(e) => ubah("calculation_type", e.target.value)}
                />
                {Form.calculation_type !== "derived" &&
                  Form.calculation_type !== "manual" && (
                    <Input
                      judul="Source Fields * (pisahkan dengan koma)"
                      type="text"
                      placeholder="water_used_l, weight_kg"
                      value={Form.source_fields}
                      onChange={(e) => ubah("source_fields", e.target.value)}
                    />
                  )}
                {Form.calculation_type === "derived" && (
                  <>
                    <Input
                      judul="Formula *"
                      type="textarea"
                      placeholder="(water_used_l / weight_kg)"
                      value={Form.formula_expression}
                      onChange={(e) => ubah("formula_expression", e.target.value)}
                    />
                    <div className="mb-3" style={{ fontSize: "10px" }}>
                      <div className="fw-bold">Variabel tersedia</div>
                      <div className="text-muted">
                        {(formulaVariables?.variabel || []).join(", ") || "-"}
                      </div>
                      <div className="fw-bold mt-1">Fungsi</div>
                      <div className="text-muted">
                        {(formulaVariables?.fungsi || []).join(", ") || "-"}
                      </div>
                    </div>
                  </>
                )}
                <Input
                  judul="Basis Alokasi"
                  type="select"
                  data={ALLOCATION}
                  value={Form.allocation_basis}
                  onChange={(e) => ubah("allocation_basis", e.target.value)}
                />
                <Input
                  judul="Metode Agregasi"
                  type="select"
                  data={AGGREGATION}
                  value={Form.aggregation_method}
                  onChange={(e) => ubah("aggregation_method", e.target.value)}
                />
              </Seksi>

              <Seksi judul="Tampilan">
                <Input
                  judul="Cakupan Tampilan"
                  type="select"
                  data={DISPLAY_SCOPE}
                  value={Form.display_scope}
                  onChange={(e) => ubah("display_scope", e.target.value)}
                />
                <Input
                  judul="Tampil ke Pelanggan"
                  type="aktif"
                  checked={!!Form.visible_customer}
                  onChange={(e) => ubah("visible_customer", e.target.checked)}
                />
                <Input
                  judul="Tampil ke Admin"
                  type="aktif"
                  checked={!!Form.visible_admin}
                  onChange={(e) => ubah("visible_admin", e.target.checked)}
                />
                <Input
                  judul="Urutan Tampil"
                  type="number"
                  value={Form.sort_order}
                  onChange={(e) => ubah("sort_order", e.target.value)}
                />
              </Seksi>

              <Seksi judul="Tata Kelola">
                <Input
                  judul="Tier Minimum untuk Publish"
                  type="select"
                  data={TIER}
                  value={Form.min_quality_tier_to_publish}
                  onChange={(e) =>
                    ubah("min_quality_tier_to_publish", e.target.value)
                  }
                />
                <Input
                  judul="Catatan Perubahan"
                  type="textarea"
                  value={Form.catatan_perubahan}
                  onChange={(e) => ubah("catatan_perubahan", e.target.value)}
                />
                {data === null && (
                  <Input
                    judul="Izinkan versi baru untuk code yang sudah ada"
                    type="aktif"
                    checked={VersiBaru}
                    onChange={(e) => setVersiBaru(e.target.checked)}
                  />
                )}
              </Seksi>
            </Col>
          </Row>
        </fieldset>

        {draft && (
          <div className="d-flex justify-content-end">
            <button className="btn btn-sm btn-secondary me-2" onClick={handleClose}>
              Batal
            </button>
            <button className="btn btn-sm btn-primary" onClick={simpan}>
              Simpan Draft
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ModalIndikator;
